import { useEffect, useRef, useState } from "react";
import Icon from "@/shared/ui/Icon";
import Button from "@/shared/ui/button/Button";

interface ColorSwitcherProps {
  value: number;
  variants: string[];
  className?: string;
  onChange?: (index: number) => void;
}

export default function ColorSwitcher({
  value,
  variants,
  className,
  onChange,
}: ColorSwitcherProps): JSX.Element {
  const [currentIndex, setCurrentIndex] = useState<number>(value);
  const isClicked = useRef<boolean>(false);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const prevTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const prevIntervalDuration = useRef<number>(400);

  const nextTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const nextIntervalDuration = useRef<number>(400);

  useEffect(() => {
    if (variants.length === 0) return;
    if (value !== currentIndex) {
      setCurrentIndex(value);
    }
  }, [value]);

  useEffect(() => {
    if (variants.length === 0) return;
    if (onChange && currentIndex !== value) {
      onChange(currentIndex);
    }
  }, [currentIndex]);

  useEffect(() => {
    if (buttonRefs.current[currentIndex] && containerRef.current) {
      const button = buttonRefs.current[currentIndex];
      const container = containerRef.current;

      const buttonLeft = button.offsetLeft;
      const buttonRight = buttonLeft + button.offsetWidth;

      const containerLeft = container.scrollLeft;
      const containerRight = containerLeft + container.offsetWidth;

      // Check if the button is out of view
      if (buttonLeft < containerLeft || buttonRight > containerRight) {
        container.scrollTo({
          left: buttonLeft - container.offsetWidth / 2,
        });
      }
    }
  }, [currentIndex, onChange, value, variants]);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? variants.length - 1 : prevIndex - 1));
    isClicked.current = true;
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex === variants.length - 1 ? 0 : prevIndex + 1));
    isClicked.current = true;
  };

  const repeatPrevAction = () => {
    handlePrev();
    prevIntervalDuration.current = Math.max(50, prevIntervalDuration.current - 50);
    prevTimeoutId.current = setTimeout(repeatPrevAction, prevIntervalDuration.current);
  };

  const handlePrevMouseDown = () => {
    prevIntervalDuration.current = 400;
    handlePrev();
    prevTimeoutId.current = setTimeout(repeatPrevAction, prevIntervalDuration.current);
  };

  const handlePrevMouseUp = () => {
    if (prevTimeoutId.current) {
      clearTimeout(prevTimeoutId.current);
      prevTimeoutId.current = null;
    }
  };

  const repeatNextAction = () => {
    handleNext();
    nextIntervalDuration.current = Math.max(50, nextIntervalDuration.current - 50);
    nextTimeoutId.current = setTimeout(repeatNextAction, nextIntervalDuration.current);
  };

  const handleNextMouseDown = () => {
    nextIntervalDuration.current = 400;
    handleNext();
    nextTimeoutId.current = setTimeout(repeatNextAction, nextIntervalDuration.current);
  };

  const handleNextMouseUp = () => {
    if (nextTimeoutId.current) {
      clearTimeout(nextTimeoutId.current);
      nextTimeoutId.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (prevTimeoutId.current) clearTimeout(prevTimeoutId.current);
      if (nextTimeoutId.current) clearTimeout(nextTimeoutId.current);
    };
  }, []);

  if (variants.length === 0) {
    return <div>No colors available</div>;
  }

  return (
    <div className={`flex items-center gap-1.5 ${className || ""}`}>
      <Button
        onMouseDown={handlePrevMouseDown}
        onMouseUp={handlePrevMouseUp}
        onMouseLeave={handlePrevMouseUp}
        onClick={() => {}}
        icon
        variant={null}
        color={null}
        className="h-9 variant-matched variant-solid hover-variant-accent hover-variant-solid-gradient"
      >
        <Icon name="triangle-right" className="w-2.5 -scale-x-100" />
      </Button>

      <div
        className="flex self-stretch items-center gap-0.5 w-[7.375rem] overflow-hidden relative"
        ref={containerRef}
      >
        {variants.map((color, index) => (
          // eslint-disable-next-line react/button-has-type
          <button
            key={color}
            ref={(el) => {
              buttonRefs.current[index] = el;
              return el;
            }}
            className={`w-7 h-7 shrink-0 grow rounded-xl ${
              currentIndex === index
                ? "border-2 outline-2 outline-black outline -outline-offset-4 border-white"
                : ""
            }`}
            style={{ backgroundColor: color }}
            aria-label={`Color ${index + 1}`}
            aria-pressed={currentIndex === index}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </div>

      <Button
        onMouseDown={handleNextMouseDown}
        onMouseUp={handleNextMouseUp}
        onMouseLeave={handleNextMouseUp}
        onClick={() => {}}
        icon
        variant={null}
        color={null}
        className="h-9 variant-matched variant-solid hover-variant-accent hover-variant-solid-gradient"
      >
        <Icon name="triangle-right" className="w-2.5" />
      </Button>
    </div>
  );
}
